import { Controller } from "@hotwired/stimulus";
import { Modal } from "bootstrap";
import { clientDeviceMetadata } from "../gauge_client_device_metadata";
import { fileSafeName, isSupportedUploadedImage, prepareUploadedPhotoFile } from "../gauge_capture/photo_file_processor";
import { readUploadedPhotoMetadata } from "../gauge_photo_metadata";

// Connects to data-controller="gauge-capture"
export default class extends Controller {
  static targets = [
    "modal", // Modal content targets
    "modalTitle",
    "modalSubtitle",
    "modalFooter",
    "message", // Alert message in the modal body
    "webcam", // Webcam and capture targets
    "video",
    "canvasFrame",
    "canvas",
    "captureButton", // Footer buttons
    "retakeButton",
    "nextButton",
    "approveButton",
    "progressContainer", // Upload progress bar container and bar
    "progress",
    "analyzerForm", // Form used only for photo analyzer processing
    "analyzerPhotoInput",
    "uploadPhotoInput",
    "analyzerResults",
    "analyzerError",
    "newGaugeReadingForm", // Form that will create the new gauge reading
    "latitudeInput", // Form hidden input
    "longitudeInput",
    "accuracyInput",
    "altitudeInput",
    "photoCapturedAtInput",
    "photoSourceInput",
    "photoMetadataInput",
    "clientDeviceMetadataInput",
    "latitudeText", // Read-only text below the form
    "longitudeText",
    "accuracyText",
    "altitudeText",
    "photoCapturedAtText",
    "mapLink", // Link to Google Maps with the captured coordinates
    "savedNotice", // Success notice shown after the gauge reading is saved
  ];

  static values = {
    projectName: String,
    loaderSrc: String,
    latitude: Number,
    longitude: Number,
    accuracy: Number,
    altitude: Number,
  };

  // Runs automatically when Stimulus connects this controller to the section.
  // The saved location values are filled during submitPhoto() and later copied
  // into the Turbo-rendered new gauge reading form when analyzer results arrive.
  connect() {
    this.clearSavedGaugeReadingLocationValues();
    this.requiresCloseConfirmation = false;
    this.modalCloseTimeout = null;
    this.photoSourceMode = "camera";
    this.uploadedPhotoFile = null;
    this.turboProgressSyncFrame = null;
  }

  // Runs when Turbo inserts the New Gauge Reading Form from
  // photo_analyzer_results.turbo_stream.erb. At this point the hidden location fields
  // exist, so the saved capture metadata can be copied into the form.
  newGaugeReadingFormTargetConnected() {
    // requestAnimationFrame(() => this.populateNewGaugeReadingForm());
    this.populateNewGaugeReadingForm();
    this.toggleOtherOptionFields();
    this.showAnalyzerReviewMode();
  }

  // Runs when the analyzer job broadcasts the error partial into the modal.
  analyzerErrorTargetConnected() {
    this.showAnalyzerErrorMode();
  }

  // Runs when create.turbo_stream.erb inserts the successful save notice.
  savedNoticeTargetConnected() {
    this.requiresCloseConfirmation = false;
    this.retakeButtonTarget.hidden = true;
    this.nextButtonTarget.hidden = true;
    this.approveButtonTarget.hidden = true;
    this.progressContainerTarget.hidden = true;
    this.modalFooterTarget.classList.remove("show-progress");
    this.clearScheduledModalClose();
    this.modalCloseTimeout = window.setTimeout(() => this.closeModal(), 2000);
  }

  // Starts the capture flow when the Bootstrap modal finishes opening.
  // Called by shown.bs.modal->gauge-capture#modalOpened in _section.html.erb.
  // The user should first see a plain permission message. Only after both
  // browser permissions succeed do we show the live video and enable Capture.
  modalOpened() {
    if (this.uploadedPhotoFile) {
      this.showUploadedPhotoReview();
      return;
    }

    // Reset modal state before requesting browser permissions.
    // A previous open might have left a stream, canvas preview, or error text
    // behind. Resetting here makes every modal open start from the same state.
    this.prepareForNewCapture("Allow camera and location access to capture a gauge reading.");

    // Request camera and location independently.
    // Promise.allSettled waits for both permission requests to finish.
    Promise.allSettled([
      // Camera request result becomes cameraResult below.
      // Keep the stream on this.stream as soon as it opens. If location fails
      // afterward, handlePermissionError can still stop the camera cleanly.
      this.requestCamera().then((stream) => {
        this.stream = stream;
        return stream;
      }),

      this.requestLocation().then((position) => {
        this.position = position;
        return position;
      }),
    ])
      .then(([cameraResult, locationResult]) => {
        // Do not show video unless both permissions are fulfilled.
        if (cameraResult.status === "rejected" || locationResult.status === "rejected") {
          this.handlePermissionError(cameraResult, locationResult);
          return null;
        }

        // Camera is ready and location is available.
        // Attach the stream to the video tag, remove the message, then start
        // playback. Safari/iOS generally requires playsinline on the video tag.
        return this.showLiveCamera(cameraResult.value);
      })
      .then((playResult) => {
        // Capture stays disabled if the permission gate returned null.
        if (playResult === null) return;

        // User can capture only after video playback starts.
        this.enableCaptureButton();
      })
      .catch((err) => {
        // Last-resort runtime failure handler.
        // Permission failures are normally handled above. This catches things
        // like video.play() failing after permissions have already succeeded.
        console.error("An error occurred: " + err);
        this.showCaptureMode();
        this.stopCamera();
      });
  }

  // Refreshes location, then captures the current live video frame and shows it on the review canvas.
  // Called by the Capture button through data-action="gauge-capture#capturePhoto".
  async capturePhoto() {
    // Guard against capturing before the video has dimensions.
    // This can happen if the user clicks too quickly or the stream has not
    // finished loading metadata yet.
    if (!this.videoTarget.videoWidth || !this.videoTarget.videoHeight) return;

    // Prevent duplicate captures while the fresh location lookup is running.
    this.captureButtonTarget.disabled = true;

    try {
      // Capture-time location is more accurate than modal-open location if the
      // user moved after opening the modal.
      this.position = await this.requestLocation();
    } catch (err) {
      // Keep the modal-open location as a fallback if the fresh lookup fails.
      console.error("Unable to refresh location: " + err);
      this.messageTarget.hidden = false;
      this.messageTarget.textContent = "Location could not be refreshed. The captured reading will use the location from when the modal was opened.";
    }

    // Match canvas size to actual camera frame.
    // Drawing at the stream's native size keeps the captured photo sharp.
    this.drawVideoFrameToCanvas();
    this.savedPhotoCapturedAt = new Date().toISOString();
    this.savedPhotoSource = "camera";
    this.photoSourceMode = "camera";
    this.uploadedPhotoFile = null;

    // Swap live preview for captured frame.
    // Once the canvas contains the photo, the live camera can be stopped.
    this.showReviewMode();
    this.stopCamera();
  }

  // Restarts the capture flow by requesting a fresh camera stream.
  // Called by the Retake button through data-action="gauge-capture#retakePhoto".
  retakePhoto() {
    if (this.photoSourceMode === "upload") {
      this.chooseUploadPhoto();
      return;
    }

    // Release any old stream before opening a fresh one.
    this.stopCamera();

    // Return the footer and media area to live-camera mode while the camera restarts.
    this.prepareForNewCapture("Starting camera...");

    // Request a new camera stream using the same camera preference as the first capture.
    this.requestCamera()
      .then((stream) => {
        // Save the stream so modal close and later captures can stop it cleanly.
        this.stream = stream;

        // Attach the stream to the video element and start playback.
        return this.showLiveCamera(stream);
      })
      .then(() => {
        // Re-enable the shutter only after the browser confirms video playback started.
        this.enableCaptureButton();
      })
      .catch((err) => {
        // Keep the modal in a blocked capture state if the camera cannot restart.
        console.error("An error occurred: " + err);
        this.messageTarget.hidden = false;
        this.messageTarget.textContent = "Camera access is required to retake the photo.";
        this.videoTarget.hidden = true;
        this.captureButtonTarget.disabled = true;
        this.stopCamera();
      });
  }

  // Shrinks the captured photo, fills the analyzer form, and prepares upload.
  // Called by the Next button in the Modal.
  // Submits to GaugeReadingsController#analyze_photo with one parameter:
  // - photo (the captured canvas image as a JPEG blob in the file-only analyzer form)
  async submitPhoto() {
    if (!this.canvasTarget.width || !this.canvasTarget.height) return;

    if (this.uploadedPhotoFile) {
      this.nextButtonTarget.disabled = true;

      if (!this.hasSavedCoordinates()) {
        try {
          this.showUploadLocationMode();
          this.position = await this.requestLocation();
        } catch (err) {
          console.error("Unable to get location for uploaded photo: " + err);
          this.showUploadError("Location access is required when the uploaded photo does not include GPS metadata.");
          return;
        }
      }
    }

    // Show progress bar
    this.showUploadMode();

    if (this.uploadedPhotoFile) {
      this.populateAnalyzerFormWithFile(this.uploadedPhotoFile);
      this.storeGaugeReadingLocationValues({ preserveExisting: true });
      this.analyzerFormTarget.requestSubmit();
      return;
    }

    // TODO: If analyzer performance is a concern, call reduceCanvasSize() from
    // photo_file_processor before converting the canvas to a JPEG blob.

    // Convert the canvas image to a JPEG blob
    this.canvasTarget.toBlob(
      (blob) => {
        if (!blob) {
          this.showUploadError("The captured photo could not be prepared for upload.");
          return;
        }

        this.populateAnalyzerForm(blob); // Populate the file-only analyzer form with the captured photo blob
        this.storeGaugeReadingLocationValues(); // Save geolocation metadata until the analyzer result form is rendered.
        this.analyzerFormTarget.requestSubmit(); // Submit the analyzer form, which submits to GaugeReadingsController#analyze_photo.
        // The analyzer runs the photo through the analyzer job, then updates the modal with results and a button to submit the new gauge reading form.
      },
      "image/jpeg",
      0.9,
    );
  }

  // Restores the upload controls if the analyzer photo upload request fails.
  // Called by turbo:submit-end on gauge_photo_analyzer_form.
  analyzerPhotoUploadEnd(event) {
    this.stopTurboProgressSync();

    if (event.detail.success) {
      this.setUploadProgress(100);
      // Canvas animation to show photo is being processed by AI.
      this.setCanvasProcessing(true);
      this.requiresCloseConfirmation = true;
      return;
    }

    this.showUploadError("Gauge photo could not be uploaded. Please retake the photo and try again.");
  }

  // Mirrors Turbo Drive's built-in request progress into the modal footer bar.
  // Called by showUploadMode() while the analyzer upload form is submitting.
  startTurboProgressSync() {
    this.stopTurboProgressSync();

    const syncProgress = () => {
      const turboProgressBar = document.querySelector(".turbo-progress-bar");
      const turboProgressWidth = parseFloat(turboProgressBar?.style.width);

      if (!Number.isNaN(turboProgressWidth)) this.setUploadProgress(turboProgressWidth);

      this.turboProgressSyncFrame = window.requestAnimationFrame(syncProgress);
    };

    this.turboProgressSyncFrame = window.requestAnimationFrame(syncProgress);
  }

  // Stops copying Turbo Drive progress into the modal footer bar.
  // Called when the analyzer upload request ends or the modal leaves upload mode.
  stopTurboProgressSync() {
    if (!this.turboProgressSyncFrame) return;

    window.cancelAnimationFrame(this.turboProgressSyncFrame);
    this.turboProgressSyncFrame = null;
  }

  // Updates the modal progress bar width and ARIA value together.
  // Called by the Turbo progress sync loop and upload lifecycle transitions.
  setUploadProgress(progress) {
    const normalizedProgress = Math.max(0, Math.min(100, Math.round(progress)));

    this.progressTarget.style.width = `${normalizedProgress}%`;
    this.progressTarget.setAttribute("aria-valuenow", normalizedProgress);
    this.progressContainerTarget.setAttribute("aria-valuenow", normalizedProgress);
  }

  // Shows only the layout selector and reading fields for the selected analyzer-detected device.
  // Called by the device select (gauge_reading[device_type]) rendered in the analyzer results partial (photo_analyzer_results.turbo_stream.erb).
  updateDeviceFields(event) {
    const selectedDevice = event.target.value;

    // Show the layout selector for the chosen device type only.
    this.element.querySelectorAll("[data-device-layout-field]").forEach((field) => {
      const isSelectedDevice = field.dataset.deviceLayoutField === selectedDevice;

      field.hidden = !isSelectedDevice;
      field.querySelectorAll("input, select, textarea").forEach((input) => {
        // Disabled controls are ignored when the new gauge reading form submits.
        input.disabled = !isSelectedDevice;
      });
    });

    // Show reading inputs that match both the selected device and its current layout.
    this.element.querySelectorAll("[data-device-field]").forEach((field) => {
      const isSelectedDevice = field.dataset.deviceField === selectedDevice;
      const selectedLayout = this.selectedLayoutForDevice(selectedDevice);
      const isSelectedLayout = field.dataset.layoutField === selectedLayout;
      const shouldShowField = isSelectedDevice && isSelectedLayout;

      field.hidden = !shouldShowField;
      field.querySelectorAll("input, select, textarea").forEach((input) => {
        // Disable inputs that do not belong to the selected device type and layout.
        // This ensures that only the visible reading values are submitted in the new gauge reading form.
        input.disabled = !shouldShowField;
      });
    });

    this.toggleKnownDeviceFields(selectedDevice);
    this.toggleOtherOptionFields();
    this.toggleApproveButtonForDevice(selectedDevice);
  }

  // Shows only the reading fields for the selected device layout.
  // Called by layout selects rendered for devices with multiple supported LCD layouts.
  updateLayoutFields(event) {
    const selectedDevice = event.target.dataset.deviceLayoutSelect;
    const selectedLayout = event.target.value;

    this.element.querySelectorAll("[data-device-field]").forEach((field) => {
      const shouldShowField = field.dataset.deviceField === selectedDevice && field.dataset.layoutField === selectedLayout;

      field.hidden = !shouldShowField;
      field.querySelectorAll("input, select, textarea").forEach((input) => {
        input.disabled = !shouldShowField;
      });
    });
  }

  // Applies Bootstrap validation to the analyzer review form before Turbo submits it.
  // Called by submit on the new_gauge_reading_form rendered in photo_analyzer_results.turbo_stream.erb.
  validateGaugeReadingForm(event) {
    const form = event.target;

    form.classList.add("was-validated");

    if (this.gaugeReadingFormIsValid(form)) return;

    event.preventDefault();
    event.stopPropagation();
    this.focusFirstInvalidGaugeReadingField(form);
  }

  // Stops the camera stream when the Bootstrap modal closes.
  // Called by hidden.bs.modal->gauge-capture#modalClosed in _section.html.erb.
  modalClosed() {
    this.clearScheduledModalClose();
    this.stopTurboProgressSync();
    this.stopCamera();
    this.requiresCloseConfirmation = false;
    this.photoSourceMode = "camera";
    this.uploadedPhotoFile = null;
    if (this.hasUploadPhotoInputTarget) this.uploadPhotoInputTarget.value = "";
  }

  // Confirms dismissal while the user is reviewing extracted gauge values.
  // Called by hide.bs.modal->gauge-capture#confirmModalClose in _section.html.erb.
  confirmModalClose(event) {
    if (!this.requiresCloseConfirmation) return;

    if (!window.confirm("Are you sure you want to close this gauge reading review?")) {
      event.preventDefault();
    }
  }

  // Opens the hidden local-image picker.
  // Called by the Upload Photo button and by the Change Photo review button.
  chooseUploadPhoto() {
    this.uploadPhotoInputTarget.click();
  }

  // Stores a selected image locally, then opens the modal for review.
  // The file is not uploaded until the user clicks Next.
  async uploadPhotoSelected(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isSupportedUploadedImage(file)) {
      window.alert("Please choose an image file.");
      event.target.value = "";
      return;
    }

    this.stopCamera();
    this.photoSourceMode = "upload";
    this.savedPhotoCapturedAt = new Date().toISOString();
    this.savedPhotoSource = "upload";
    const { metadata, values } = await readUploadedPhotoMetadata(file);
    this.savedPhotoMetadata = metadata;
    this.applyUploadedPhotoMetadataToSavedValues(values);

    try {
      this.uploadedPhotoFile = await prepareUploadedPhotoFile(file, fileSafeName(this.projectNameValue));
    } catch (error) {
      console.error("Unable to prepare uploaded photo: " + error);
      window.alert("The selected HEIC image could not be converted. Please choose another image.");
      event.target.value = "";
      return;
    }

    if (this.modalTarget.classList.contains("show")) {
      this.showUploadedPhotoReview();
      return;
    }

    Modal.getOrCreateInstance(this.modalTarget).show();
  }

  //////////////////////////////////////////////////////////////////
  // Internal helper functions
  // Methods below this point are called only by this controller.
  // They are not wired directly to data-action attributes in the ERB.
  //////////////////////////////////////////////////////////////////

  // Programmatically closes the Bootstrap modal.
  // Intended for internal controller flows that need to dismiss the modal.
  closeModal() {
    Modal.getOrCreateInstance(this.modalTarget).hide();
  }

  clearScheduledModalClose() {
    if (!this.modalCloseTimeout) return;

    window.clearTimeout(this.modalCloseTimeout);
    this.modalCloseTimeout = null;
  }

  // Stops the active camera stream and releases browser camera resources.
  // Called by capturePhoto(), retakePhoto(), modalClosed(), and error paths.
  stopCamera() {
    // No active stream means there is nothing to release.
    if (!this.stream) return;

    // Stop every MediaStreamTrack.
    // Browsers keep the camera active until all tracks are stopped.
    this.stream.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.videoTarget.srcObject = null;
  }

  // Requests camera permission and prefers the rear camera when available.
  // Called by modalOpened() and retakePhoto() before showing live video.
  requestCamera() {
    // API availability check.
    // getUserMedia requires HTTPS or localhost in modern browsers. If the API
    // is unavailable, treat it like camera access cannot be granted.
    if (!navigator.mediaDevices?.getUserMedia) {
      return Promise.reject(new Error("Camera access is not supported by this browser."));
    }

    // Prefer rear camera on phones and tablets.
    // facingMode: "environment" is the standard hint for the rear camera.
    // "ideal" means "use this if possible", so desktops and single-camera
    // devices are not rejected just because they lack a rear camera.
    return navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          // width: { ideal: 1920 },
          // height: { ideal: 1080 },
        },
        audio: false,
      })
      .catch((err) => {
        // Fallback when the preferred camera cannot be opened.
        // Some browsers/devices cannot satisfy the rear-camera constraint.
        // In that case, falling back to any camera is better than blocking
        // users on laptops, desktops, or older mobile browsers.
        if (this.shouldFallbackToAnyCamera(err)) {
          return navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
        }

        return Promise.reject(err);
      });
  }

  // Requests browser location permission for capture metadata.
  // Called by modalOpened() before the capture button is enabled.
  requestLocation() {
    return new Promise((resolve, reject) => {
      // API availability check.
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by this browser."));
        return;
      }

      // Browser permission prompt happens here.
      // The resolved position is not used yet, but successful resolution tells
      // the controller that location access is available for this capture.
      navigator.geolocation.getCurrentPosition(resolve, reject);
    });
  }

  // Shows a specific permission error and blocks capture when permissions fail.
  // Called by modalOpened() after Promise.allSettled receives a rejected result.
  handlePermissionError(cameraResult, locationResult) {
    // Convert allSettled result objects into readable booleans.
    const cameraDenied = cameraResult.status === "rejected";
    const locationDenied = locationResult.status === "rejected";

    // Pick the most specific message possible.
    if (cameraDenied && locationDenied) {
      this.messageTarget.textContent = "Camera and location access are required to capture a gauge reading.";
    } else if (cameraDenied) {
      this.messageTarget.textContent = "Camera access is required to capture a gauge reading.";
    } else if (locationDenied) {
      this.messageTarget.textContent = "Location access is required to capture a gauge reading.";
    }

    // Keep modal in blocked state.
    // If either permission is missing, do not show a stale video frame and do
    // not allow the Capture button to run.
    this.showCaptureMode();
    this.stopCamera();
  }

  // Decides whether to retry camera access without rear-camera constraints.
  // Called by requestCamera() when the preferred camera request fails.
  shouldFallbackToAnyCamera(err) {
    // Only fallback for device/constraint-style failures.
    // Permission denials should not fallback. If the user blocks the camera,
    // the correct behavior is to show the camera access message.
    return ["OverconstrainedError", "NotFoundError", "NotReadableError"].includes(err.name);
  }

  // Resets the modal controls for taking a fresh photo.
  // Called by prepareForNewCapture(), permission error handling, and runtime error handling.
  showCaptureMode() {
    this.messageTarget.hidden = false; // Show modal message
    this.hideCanvasPreview();
    // Hide the live video until a stream is attached and playback begins.
    this.videoTarget.hidden = true;

    this.captureButtonTarget.hidden = false; // Show the shutter button
    this.captureButtonTarget.disabled = true; // Disable the shutter until camera playback is ready.

    // Hide the post-capture review controls.
    this.retakeButtonTarget.hidden = true;
    this.nextButtonTarget.hidden = true;
    this.approveButtonTarget.hidden = true;
    this.progressContainerTarget.hidden = true;
    this.removeUploadLayoutClasses();
  }

  // Clears stale state before opening or restarting the capture flow.
  // Called by modalOpened() and retakePhoto().
  prepareForNewCapture(message) {
    this.requiresCloseConfirmation = false;
    this.clearScheduledModalClose();

    // Drop any stale stream reference from a previous modal session.
    this.stream = null;
    this.photoSourceMode = "camera";
    this.uploadedPhotoFile = null;
    this.savedPhotoSource = "camera";
    if (this.hasUploadPhotoInputTarget) this.uploadPhotoInputTarget.value = "";

    // Set the message that should be visible while permissions or camera startup run.
    this.messageTarget.textContent = message;
    this.messageTarget.classList.remove("alert-danger");
    this.messageTarget.classList.add("alert-info");
    this.modalTitleTarget.textContent = "Capture Gauge Reading";
    this.modalSubtitleTarget.textContent = "Allow camera and location access to capture a gauge reading.";

    // Put the footer and media targets back into live-capture mode.
    this.showCaptureMode();

    // Clear any old captured frame from the canvas.
    this.clearCanvas();
    this.clearResults();
    this.clearSavedGaugeReadingLocationValues();
  }

  // Enables the shutter once live camera playback is confirmed.
  // Called by modalOpened() and retakePhoto() after showLiveCamera() resolves.
  enableCaptureButton() {
    // Enable the shutter only when a real video stream is playing.
    this.captureButtonTarget.disabled = false;
  }

  // Switches the modal from live camera mode to captured-photo review mode.
  // Called by capturePhoto() after drawing the video frame to the canvas.
  showReviewMode() {
    // Hide live video because the camera frame has already been captured.
    this.videoTarget.hidden = true;

    // Show the canvas that contains the captured still frame.
    this.showCanvasPreview();

    // Hide the shutter so the user does not capture again from a stopped stream.
    this.captureButtonTarget.hidden = true;

    // Update modal title and subtitle to clarify that the user is reviewing a still photo, not live video.
    this.modalTitleTarget.textContent = "Review Gauge Photo";
    this.modalSubtitleTarget.textContent = this.photoSourceMode === "upload" ? "Choose another photo if this one is not clear." : "Retake if it's not clear.";

    // Show the review controls for the captured still frame.
    this.retakeButtonTarget.hidden = false;
    this.setRetakeButtonText(this.photoSourceMode === "upload" ? "Change" : "Retake");
    this.nextButtonTarget.hidden = false;
    this.approveButtonTarget.hidden = true;
    this.progressContainerTarget.hidden = true;
    this.removeUploadLayoutClasses();
  }

  // Shows the modal state used while the file-only analyzer form is uploading.
  // Called by submitPhoto() after the user clicks Next on the review screen.
  // The captured photo remains visible, review buttons are hidden, and the
  // Bootstrap progress bar follows Turbo's built-in request progress while the job starts.
  showUploadMode() {
    // Update modal header
    if (this.hasLoaderSrcValue) {
      const loader = document.createElement("img");
      loader.src = this.loaderSrcValue;
      loader.alt = "";
      loader.className = "gauge-photo-modal-title-loader";
      loader.setAttribute("aria-hidden", "true");

      const titleText = document.createElement("span");
      titleText.textContent = "Uploading Gauge Photo";

      this.modalTitleTarget.textContent = "";
      this.modalTitleTarget.append(loader, titleText);
    } else {
      this.modalTitleTarget.textContent = "Uploading Gauge Photo";
    }

    this.modalSubtitleTarget.textContent = "Your photo is being uploaded and processed.";
    this.messageTarget.hidden = true; // Hide any previous message

    // Do not show any buttons in the footer
    this.nextButtonTarget.disabled = false;
    this.retakeButtonTarget.hidden = true;
    this.nextButtonTarget.hidden = true;
    this.approveButtonTarget.hidden = true;

    // Keep the captured photo at the same stable preview height used during review.
    this.addCapturedPhotoCompactClass();
    // show-progress class adjusts modal footer grid template so progress bar can take up all the space.
    this.modalFooterTarget.classList.add("show-progress");

    // Show the progress bar
    this.progressContainerTarget.hidden = false;
    this.setUploadProgress(0);
    this.startTurboProgressSync();
  }

  // Shows the footer controls used after analyzer results have loaded.
  // Called when Turbo connects the New Gauge Reading Form inside the modal.
  showAnalyzerReviewMode() {
    this.requiresCloseConfirmation = true;

    this.messageTarget.hidden = true;

    // Analyzer work is complete, so hide upload progress and restore footer spacing.
    this.stopTurboProgressSync();
    this.progressContainerTarget.hidden = true;
    // this.removeUploadLayoutClasses();
    this.modalFooterTarget.classList.remove("show-progress");
    this.setCanvasProcessing(false);

    // Let the user retake the photo or approve the populated form.
    this.retakeButtonTarget.hidden = false;
    this.setRetakeButtonText(this.photoSourceMode === "upload" ? "Change" : "Retake");
    this.nextButtonTarget.hidden = true;
    this.toggleApproveButtonForDevice();
  }

  // Hides approval until the reviewer selects a known device type.
  // Called when analyzer results first load and whenever the device select changes.
  toggleApproveButtonForDevice(selectedDevice = null) {
    const device = selectedDevice || this.newGaugeReadingFormTarget.querySelector("[name='gauge_reading[device_type]']")?.value;

    this.approveButtonTarget.hidden = device === "Unknown";
  }

  // Shows common required fields only when the reviewer has selected a supported device.
  toggleKnownDeviceFields(selectedDevice = null) {
    const device = selectedDevice || this.newGaugeReadingFormTarget.querySelector("[name='gauge_reading[device_type]']")?.value;
    const shouldShowFields = device !== "Unknown";

    this.element.querySelectorAll("[data-known-device-fields]").forEach((fieldGroup) => {
      fieldGroup.hidden = !shouldShowFields;
      fieldGroup.querySelectorAll("input, select, textarea").forEach((input) => {
        input.disabled = !shouldShowFields;
      });
    });
  }

  // Shows the freeform field attached to a dropdown only when that dropdown is set to Other.
  // Called when Surface Type or Material Tested changes, and when analyzer results first render.
  toggleOtherOptionFields() {
    if (!this.hasNewGaugeReadingFormTarget) return;

    this.element.querySelectorAll("[data-other-option-field]").forEach((fieldGroup) => {
      const select = this.newGaugeReadingFormTarget.elements[fieldGroup.dataset.otherOptionField];
      const shouldShowField = select && !select.disabled && select.value.toLowerCase() === "other";

      fieldGroup.hidden = !shouldShowField;
      fieldGroup.querySelectorAll("input, select, textarea").forEach((input) => {
        input.disabled = !shouldShowField;
        input.required = shouldShowField;
        if (!shouldShowField) input.value = "";
      });
    });
  }

  // Checks only enabled fields, so hidden device/layout branches can stay required
  // without blocking submission.
  gaugeReadingFormIsValid(form) {
    return Array.from(form.elements).every((field) => field.disabled || field.checkValidity());
  }

  // Finds the first invalid visible/enabled analyzer result input and brings it into view.
  focusFirstInvalidGaugeReadingField(form) {
    // Convert the form's HTMLFormControlsCollection into an array so we can use find().
    const invalidField = Array.from(form.elements).find((field) => !field.disabled && !field.checkValidity());

    // If every enabled field is valid, there is nothing to focus or scroll to.
    if (!invalidField) return;

    // Move keyboard focus to the first invalid field without letting the browser jump abruptly.
    invalidField.focus({ preventScroll: true });

    // Smoothly center the invalid field in the modal so the reviewer can fix it.
    invalidField.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  // Returns the selected layout for a device, whether it is stored in a visible select or hidden input.
  // Called when switching device types so layout-specific reading fields stay in sync.
  selectedLayoutForDevice(device) {
    return this.newGaugeReadingFormTarget.querySelector(`[data-device-layout-select="${CSS.escape(device)}"]`)?.value;
  }

  // Shows the controls used when the analyzer job fails after upload.
  // Called when Turbo inserts the analyzer error partial into the modal.
  showAnalyzerErrorMode() {
    this.requiresCloseConfirmation = false;
    this.stopTurboProgressSync();
    this.messageTarget.hidden = true;

    this.modalTitleTarget.textContent = "Gauge Photo Analyzer Failed";
    this.modalSubtitleTarget.textContent = this.photoSourceMode === "upload" ? "Choose another photo and try again." : "Retake the photo and try again.";

    this.captureButtonTarget.hidden = true;
    this.retakeButtonTarget.hidden = false;
    this.setRetakeButtonText(this.photoSourceMode === "upload" ? "Change" : "Retake");
    this.nextButtonTarget.disabled = false;
    this.nextButtonTarget.hidden = true;
    this.approveButtonTarget.hidden = true;
    this.progressContainerTarget.hidden = true;

    this.modalFooterTarget.classList.remove("show-progress");
    this.setCanvasProcessing(false);
  }

  // Shows an upload error and restores review controls so the user can retry.
  // Called by submitPhoto() when canvas conversion fails and by analyzerPhotoUploadEnd() on upload failure.
  showUploadError(message) {
    this.stopTurboProgressSync();
    this.messageTarget.hidden = false;
    this.messageTarget.classList.remove("alert-info");
    this.messageTarget.classList.add("alert-danger");
    this.messageTarget.textContent = message;
    this.retakeButtonTarget.hidden = false;
    this.setRetakeButtonText(this.photoSourceMode === "upload" ? "Change" : "Retake");
    this.nextButtonTarget.disabled = false;
    this.nextButtonTarget.hidden = false;
    this.approveButtonTarget.hidden = true;
    this.progressContainerTarget.hidden = true;
    // this.removeUploadLayoutClasses();
    this.modalFooterTarget.classList.remove("show-progress");
    this.setCanvasProcessing(false);
  }

  // Shows a short status while uploaded-photo flow gets the location required by GaugeReading validation.
  // Called by submitPhoto() before the selected file is uploaded.
  showUploadLocationMode() {
    this.stopTurboProgressSync();
    this.modalTitleTarget.textContent = "Getting Current Location";
    this.modalSubtitleTarget.textContent = "Location is required to save this gauge reading.";

    this.messageTarget.hidden = false;
    this.messageTarget.classList.remove("alert-danger");
    this.messageTarget.classList.add("alert-info");
    this.messageTarget.textContent = "Allow location access to continue.";

    this.retakeButtonTarget.hidden = true;
    this.nextButtonTarget.hidden = false;
    this.approveButtonTarget.hidden = true;
    this.progressContainerTarget.hidden = true;
    this.modalFooterTarget.classList.remove("show-progress");
  }

  // Writes the captured JPEG blob into the file-only analyzer form.
  // Called by submitPhoto() after canvas.toBlob() succeeds.
  populateAnalyzerForm(blob) {
    const now = new Date();

    // Build a local timestamp that is safe for filenames.
    // Format: YYYY-MM-DD_HH-MM-SS.
    // Examples: 2026-05-10_09-04-07, 2026-11-03_17-42-59.
    const filenameTime =
      `${now.getFullYear()}-` +
      `${String(now.getMonth() + 1).padStart(2, "0")}-` +
      `${String(now.getDate()).padStart(2, "0")}_` +
      `${String(now.getHours()).padStart(2, "0")}-` +
      `${String(now.getMinutes()).padStart(2, "0")}-` +
      `${String(now.getSeconds()).padStart(2, "0")}`;

    // Final filename is "<safe-project-name>-<timestamp>.jpg".
    // Examples: "main-street-bridge-2026-05-10_09-04-07.jpg",
    // "project-123-north-pad-2026-11-03_17-42-59.jpg".
    const file = new File([blob], `${fileSafeName(this.projectNameValue)}-${filenameTime}.jpg`, { type: "image/jpeg" });

    // DataTransfer lets JavaScript assign a FileList to the hidden file input.
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);

    // Populate the analyzer form without asking the user to choose a file manually.
    this.analyzerPhotoInputTarget.files = dataTransfer.files;
  }

  // Writes the user-selected upload file into the analyzer form.
  // Called by submitPhoto() so the original chosen image is uploaded only after review.
  populateAnalyzerFormWithFile(file) {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);

    this.analyzerPhotoInputTarget.files = dataTransfer.files;
  }

  // Saves browser geolocation values while the analyzer request is running.
  // Called by submitPhoto() after canvas.toBlob() succeeds. The actual form
  // fields do not exist until Turbo renders photo_analyzer_results.turbo_stream.erb.
  storeGaugeReadingLocationValues({ preserveExisting = false } = {}) {
    const coordinates = this.position?.coords;

    this.savedLatitude = preserveExisting && this.savedLatitude !== "" ? this.savedLatitude : (coordinates?.latitude ?? "");
    this.savedLongitude = preserveExisting && this.savedLongitude !== "" ? this.savedLongitude : (coordinates?.longitude ?? "");
    this.savedAccuracy = preserveExisting && this.savedAccuracy !== "" ? this.savedAccuracy : (coordinates?.accuracy ?? "");
    this.savedAltitude = preserveExisting && this.savedAltitude !== "" ? this.savedAltitude : (coordinates?.altitude ?? "");
  }

  applyUploadedPhotoMetadataToSavedValues(values) {
    const { capturedAt, latitude, longitude, accuracy, altitude } = values;

    if (capturedAt) this.savedPhotoCapturedAt = capturedAt;
    if (latitude !== "") this.savedLatitude = latitude;
    if (longitude !== "") this.savedLongitude = longitude;
    if (accuracy !== "") this.savedAccuracy = accuracy;
    if (altitude !== "") this.savedAltitude = altitude;
  }

  hasSavedCoordinates() {
    return this.savedLatitude !== "" && this.savedLongitude !== "";
  }

  // Clears stored geolocation values before starting a fresh capture.
  // Called by connect() and prepareForNewCapture().
  clearSavedGaugeReadingLocationValues() {
    this.savedLatitude = "";
    this.savedLongitude = "";
    this.savedAccuracy = "";
    this.savedAltitude = "";
    this.savedPhotoCapturedAt = "";
    this.savedPhotoSource = "camera";
    this.savedPhotoMetadata = {};
  }

  // Copies saved geolocation values into the new gauge reading form.
  // Called automatically when Turbo inserts the analyzer result form.
  populateNewGaugeReadingForm() {
    if (!this.hasNewGaugeReadingFormTarget) return;
    if (!this.hasLatitudeInputTarget || !this.hasLongitudeInputTarget || !this.hasAccuracyInputTarget || !this.hasAltitudeInputTarget || !this.hasPhotoCapturedAtInputTarget || !this.hasPhotoSourceInputTarget || !this.hasPhotoMetadataInputTarget || !this.hasClientDeviceMetadataInputTarget) {
      return;
    }
    if (!this.hasLatitudeTextTarget || !this.hasLongitudeTextTarget || !this.hasAccuracyTextTarget || !this.hasAltitudeTextTarget || !this.hasPhotoCapturedAtTextTarget) return;

    this.latitudeInputTarget.value = this.savedLatitude;
    this.longitudeInputTarget.value = this.savedLongitude;
    this.accuracyInputTarget.value = this.savedAccuracy;
    this.altitudeInputTarget.value = this.savedAltitude;
    this.photoCapturedAtInputTarget.value = this.savedPhotoCapturedAt;
    this.photoSourceInputTarget.value = this.savedPhotoSource || "camera";
    this.photoMetadataInputTarget.value = JSON.stringify(this.savedPhotoMetadata || {});
    this.clientDeviceMetadataInputTarget.value = JSON.stringify(clientDeviceMetadata());

    this.latitudeTextTarget.textContent = this.formatMetadataValue(this.savedLatitude);
    this.longitudeTextTarget.textContent = this.formatMetadataValue(this.savedLongitude);
    this.accuracyTextTarget.textContent = this.formatMetadataValue(this.savedAccuracy, " m");
    this.altitudeTextTarget.textContent = this.formatMetadataValue(this.savedAltitude, " m");
    this.photoCapturedAtTextTarget.textContent = this.formatCaptureTimestamp(this.savedPhotoCapturedAt);

    // Show the map link
    if (this.hasMapLinkTarget) {
      if (this.savedLatitude && this.savedLongitude) {
        this.mapLinkTarget.href = `https://www.google.com/maps/search/?api=1&query=${this.savedLatitude},${this.savedLongitude}`;
        this.mapLinkTarget.hidden = false;
      } else {
        this.mapLinkTarget.hidden = true;
      }
    }
  }

  // Formats saved metadata for the read-only text shown beside the analyzer fields.
  // Empty values are shown plainly so workers can see when a value was unavailable.
  formatMetadataValue(value, suffix = "") {
    if (value === "" || value === null || value === undefined) return "Not available";

    return `${value}${suffix}`;
  }

  // Formats a saved photo-capture ISO timestamp for the read-only review metadata.
  // Called by populateNewGaugeReadingForm() after the analyzer result form is inserted.
  // Example return value: "Jun 5, 2026, 1:10 PM".
  formatCaptureTimestamp(value) {
    if (!value) return "Not available";

    // Intl.DateTimeFormat formats the Date for the user's current locale; undefined uses the browser locale,
    // dateStyle: "medium" gives a compact readable date, and timeStyle: "short" gives hours/minutes without seconds.
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  }

  // Removes submitted-photo sizing, processing, and footer layout classes.
  // Called by capture/review state methods when leaving analyzer mode.
  removeUploadLayoutClasses() {
    this.removeCapturedPhotoCompactClass();
    this.setCanvasProcessing(false);
    this.modalFooterTarget.classList.remove("show-progress");
  }

  addCapturedPhotoCompactClass() {
    this.canvasTarget.classList.add("captured-photo-compact");
    if (this.hasCanvasFrameTarget) this.canvasFrameTarget.classList.add("captured-photo-compact");
  }

  removeCapturedPhotoCompactClass() {
    this.canvasTarget.classList.remove("captured-photo-compact");
    if (this.hasCanvasFrameTarget) this.canvasFrameTarget.classList.remove("captured-photo-compact");
  }

  // Shows the captured canvas and its clipped overlay frame.
  showCanvasPreview() {
    this.canvasTarget.hidden = false;
    if (this.hasCanvasFrameTarget) {
      this.canvasFrameTarget.hidden = false;
    }
  }

  // Hides the captured canvas and clears processing-only visual state.
  hideCanvasPreview() {
    this.canvasTarget.hidden = true;
    if (this.hasCanvasFrameTarget) {
      this.canvasFrameTarget.hidden = true;
      this.canvasFrameTarget.classList.remove("captured-photo-compact");
    }
    this.setCanvasProcessing(false);
  }

  // Keeps the existing canvas processing class while giving the wrapper a
  // pseudo-element host for the clipped scan animation.
  setCanvasProcessing(isProcessing) {
    this.canvasTarget.classList.toggle("processing", isProcessing);
    if (this.hasCanvasFrameTarget) {
      this.canvasFrameTarget.classList.toggle("processing", isProcessing);
    }
  }

  // Draws a locally selected image file into the same review canvas used by captured photos.
  // Called by modalOpened() only for the upload-photo path.
  showUploadedPhotoReview() {
    this.prepareForUploadedPhotoReview();

    const image = new Image();
    const objectUrl = URL.createObjectURL(this.uploadedPhotoFile);

    image.onload = () => {
      this.drawImageToCanvas(image);
      URL.revokeObjectURL(objectUrl);
      this.showReviewMode();
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      this.showUploadPhotoError("The selected image could not be previewed. Please choose another image.");
    };

    image.src = objectUrl;
  }

  // Resets the modal without requesting camera or location access.
  // Called before previewing an uploaded image file.
  prepareForUploadedPhotoReview() {
    this.requiresCloseConfirmation = false;
    this.clearScheduledModalClose();
    this.stopCamera();
    this.clearCanvas();
    this.clearResults();
    this.savedPhotoSource = "upload";
    if (!this.savedPhotoCapturedAt) this.savedPhotoCapturedAt = new Date().toISOString();

    this.messageTarget.hidden = false;
    this.messageTarget.textContent = "Loading selected image...";
    this.messageTarget.classList.remove("alert-danger");
    this.messageTarget.classList.add("alert-info");

    this.modalTitleTarget.textContent = "Review Gauge Photo";
    this.modalSubtitleTarget.textContent = "Choose another photo if this one is not clear.";

    this.videoTarget.hidden = true;
    this.hideCanvasPreview();
    this.captureButtonTarget.hidden = true;
    this.retakeButtonTarget.hidden = true;
    this.nextButtonTarget.hidden = true;
    this.approveButtonTarget.hidden = true;
    this.progressContainerTarget.hidden = true;
    this.removeUploadLayoutClasses();
  }

  // Shows an error for a local uploaded image before it reaches the analyzer form.
  showUploadPhotoError(message) {
    this.messageTarget.hidden = false;
    this.messageTarget.classList.remove("alert-info");
    this.messageTarget.classList.add("alert-danger");
    this.messageTarget.textContent = message;

    this.captureButtonTarget.hidden = true;
    this.retakeButtonTarget.hidden = false;
    this.setRetakeButtonText("Change");
    this.nextButtonTarget.hidden = true;
    this.approveButtonTarget.hidden = true;
    this.progressContainerTarget.hidden = true;
    this.modalFooterTarget.classList.remove("show-progress");
  }

  // Copies a decoded uploaded image into the review canvas.
  // Called by showUploadedPhotoReview() after the browser loads the selected file.
  drawImageToCanvas(image) {
    this.canvasTarget.width = image.naturalWidth;
    this.canvasTarget.height = image.naturalHeight;
    this.canvasTarget.getContext("2d").drawImage(image, 0, 0);
    this.messageTarget.hidden = true;
  }

  setRetakeButtonText(text) {
    const label = this.retakeButtonTarget.querySelector("span");
    if (label) label.textContent = text;
  }

  // Copies the current live video frame into the review canvas.
  // Called by capturePhoto() after validating the video has dimensions.
  drawVideoFrameToCanvas() {
    // Match the canvas width to the camera frame width.
    this.canvasTarget.width = this.videoTarget.videoWidth;

    // Match the canvas height to the camera frame height.
    this.canvasTarget.height = this.videoTarget.videoHeight;

    // Draw the current video frame onto the canvas for still-photo review.
    this.canvasTarget.getContext("2d").drawImage(this.videoTarget, 0, 0);
  }

  // Attaches a camera stream to the video element and starts playback.
  // Called by modalOpened() and retakePhoto() after camera permission succeeds.
  showLiveCamera(stream) {
    // Attach the provided camera stream to the video preview.
    this.videoTarget.srcObject = stream;

    // Hide any permission or loading message once preview is ready to start.
    this.messageTarget.hidden = true;

    // Show the video element before requesting playback.
    this.videoTarget.hidden = false;

    // Keep the modal subtitle aligned with live-camera framing mode.
    this.modalSubtitleTarget.textContent = "Position the device screen in the frame";

    // Ask the browser to start playback and return the resulting promise.
    return this.videoTarget.play();
  }

  // Clears the captured photo canvas.
  // Called by prepareForNewCapture() before starting or restarting capture.
  clearCanvas() {
    // Get the canvas drawing context used for the captured photo.
    const context = this.canvasTarget.getContext("2d");

    // Clear the full canvas area so the previous captured frame is removed.
    context.clearRect(0, 0, this.canvasTarget.width, this.canvasTarget.height);
  }

  // Clears any analyzer results currently shown in the modal.
  // Called by prepareForNewCapture() before a new capture starts.
  clearResults() {
    if (!this.hasAnalyzerResultsTarget) return;

    this.analyzerResultsTarget.innerHTML = "";
  }
}
